import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Building2, MapPin, User, Sparkles, Eye, Wrench, Ticket as TicketIcon,
  Calendar, Phone, Folder, Image as ImageIcon, ClipboardList, Settings2,
  FileText, ShoppingCart, Receipt, Mic, AlertTriangle, ChevronRight, X,
  Network, Search,
} from "lucide-react";
import api from "../lib/api";

/* ─── Wave 15 — Entity Trail (linked-entity breadcrumb) — 2026-02 #73 ──
   Lives below the navbar on EVERY authenticated page. Reads the URL,
   infers the {kind,id} of what the user is viewing, calls
   /api/entity-trail/{kind}/{id}, and renders a horizontal chip strip
   showing every entity related to this one. Rose-red when the chain
   doesn't reach a customer.

   Design intent:
     • Catch orphans (anything not eventually linked to a customer).
     • Make navigation obvious — every chip is a real link.
     • Be quiet when on a global page (Dashboard, Settings, Inbox, …) —
       hide rather than yell. */
const ICONS = {
  Building2, MapPin, User, Sparkles, Eye, Wrench, Calendar, Phone, Folder,
  ImageIcon, ClipboardList, Settings2, FileText, ShoppingCart, Receipt, Mic,
  Ticket: TicketIcon,
};

const KIND_COLORS = {
  customer:        "border-emerald-400/40 bg-emerald-400/5 text-emerald-300",
  site:            "border-[#1FB6C1]/40 bg-[#1FB6C1]/5 text-[#1FB6C1]",
  contact:         "border-[#A78BFA]/40 bg-[#A78BFA]/5 text-[#A78BFA]",
  lead:            "border-[#F59E0B]/40 bg-[#F59E0B]/5 text-amber-300",
  suspect:         "border-[#A78BFA]/40 bg-[#A78BFA]/5 text-[#A78BFA]",
  jobcard:         "border-amber-500/40 bg-amber-500/5 text-amber-300",
  ticket:          "border-rose-500/40 bg-rose-500/5 text-rose-300",
  meeting:         "border-[#1FB6C1]/40 bg-[#1FB6C1]/5 text-[#1FB6C1]",
  call:            "border-[#A78BFA]/40 bg-[#A78BFA]/5 text-[#A78BFA]",
  trip:            "border-[#1FB6C1]/40 bg-[#1FB6C1]/5 text-[#1FB6C1]",
  gallery_folder:  "border-[#10B981]/40 bg-[#10B981]/5 text-[#10B981]",
  gallery_item:    "border-[#10B981]/40 bg-[#10B981]/5 text-[#10B981]",
  service_log:     "border-white/15 bg-white/5 text-[#a0aabf]",
  equipment:       "border-white/15 bg-white/5 text-[#a0aabf]",
  sales_document:  "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
  order:           "border-[#F59E0B]/40 bg-[#F59E0B]/5 text-amber-300",
  invoice:         "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
  sales_brief:     "border-[#F59E0B]/40 bg-[#F59E0B]/5 text-amber-300",
  picking_list:    "border-cyan-400/40 bg-cyan-400/5 text-cyan-300",
  voice_note:      "border-[#A78BFA]/40 bg-[#A78BFA]/5 text-[#A78BFA]",
  // Iter 411 — Procurement side of the family tree.
  supplier:            "border-fuchsia-500/40 bg-fuchsia-500/5 text-fuchsia-300",
  procurement_tracker: "border-cyan-500/40 bg-cyan-500/5 text-cyan-300",
  supplier_document:   "border-fuchsia-500/40 bg-fuchsia-500/5 text-fuchsia-300",
};

// Map from URL path → {kind, paramName}. Order matters — first match wins.
// Pages without an entity context (dashboard, settings, etc.) intentionally
// resolve to null and the breadcrumb hides itself.
const ROUTE_PATTERNS = [
  { re: /^\/customers\/([^/?#]+)/,        kind: "customer" },
  { re: /^\/sites\/([^/?#]+)/,            kind: "site" },
  { re: /^\/jobcards\/([^/?#]+)/,         kind: "jobcard" },
  { re: /^\/tickets\/([^/?#]+)/,          kind: "ticket" },
  { re: /^\/leads\/([^/?#]+)/,            kind: "lead" },
  { re: /^\/research\/lead\/([^/?#]+)/,   kind: "lead" },
  { re: /^\/trips\/([^/?#]+)/,            kind: "trip" },
  { re: /^\/orders\/([^/?#]+)/,           kind: "order" },
  { re: /^\/invoices\/([^/?#]+)/,         kind: "invoice" },
  { re: /^\/picking\/lists\/([^/?#]+)/,    kind: "picking_list" },
  { re: /^\/sales-documents\/([^/?#]+)/,   kind: "sales_document" },
  // Iter 411 (Dirk, 2026-02-28) — Family-tree breadcrumbs now cover
  // the procurement side of the graph too. Order matters:
  // /procurement is the trackers hub (no id), /procurement/{tid} is
  // the detail. Skip when the segment isn't a real id.
  { re: /^\/suppliers\/([^/?#]+)/,         kind: "supplier" },
  { re: /^\/procurement\/([^/?#]+)/,       kind: "procurement_tracker" },
];

function detectEntity(pathname) {
  for (const p of ROUTE_PATTERNS) {
    const m = pathname.match(p.re);
    // Iter 413 — Skip reserved segments like /jobcards/new,
    // /jobcards/templates (list) and /jobcards/templates/{...} — they
    // are UI pages, not entity IDs.
    if (m && m[1] && !["new", "templates"].includes(m[1])) {
      return { kind: p.kind, id: m[1] };
    }
  }
  return null;
}

export default function EntityTrail() {
  const loc = useLocation();
  const params = useParams();
  const [trail, setTrail] = useState(null);
  const [error, setError] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const [familyOpen, setFamilyOpen] = useState(false);

  const target = useMemo(() => detectEntity(loc.pathname), [loc.pathname]);

  useEffect(() => {
    if (!target) { setTrail(null); return; }
    setError("");
    setTrail(null);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/entity-trail/${target.kind}/${target.id}`);
        if (!cancelled) setTrail(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not resolve trail");
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.kind, target?.id, params]);

  // Reset the dismiss when the URL changes
  useEffect(() => { setDismissed(false); }, [loc.pathname]);

  if (!target || dismissed) return null;
  if (!trail && !error) {
    // Pre-fetch placeholder to reserve vertical space (avoids layout shift)
    return <div className="h-7 border-b border-white/5 bg-white/[0.015]" data-testid="entity-trail-loading" />;
  }
  if (error) {
    return (
      <div className="px-3 py-1 text-[10px] text-rose-300 border-b border-white/5 bg-rose-500/5 flex items-center gap-2" data-testid="entity-trail-error">
        <AlertTriangle className="w-3 h-3" />
        <span>Trail: {error}</span>
      </div>
    );
  }

  const isOrphan = !trail.has_customer && !trail.warnings.some((w) => /prospect|pre-customer|trip/i.test(w));
  const hasWarning = trail.warnings.length > 0;
  const allChips = [trail.viewing, ...trail.links];
  // Move the customer chip to position 1 (after viewing) so it's always
  // the first link the user sees.
  const cust = allChips.find((c) => c.kind === "customer");
  const ordered = cust && allChips[0] !== cust
    ? [allChips[0], cust, ...allChips.filter((c) => c !== cust && c !== allChips[0])]
    : allChips;

  const containerCls = isOrphan
    ? "border-rose-500/30 bg-rose-500/5"
    : hasWarning
      ? "border-amber-400/30 bg-amber-500/[0.03]"
      : "border-white/5 bg-white/[0.015]";

  return (
    <>
    <div
      className={`px-2 sm:px-3 py-1 border-b ${containerCls} flex items-center gap-1.5 overflow-x-auto whitespace-nowrap`}
      data-testid="entity-trail"
      data-orphan={isOrphan ? "1" : "0"}
    >
      {/* Tiny prefix label */}
      <span className="text-[9px] uppercase tracking-[0.2em] text-[#647087] shrink-0">Linked</span>
      <ChevronRight className="w-2.5 h-2.5 text-[#647087] shrink-0" />
      {ordered.map((chip, i) => {
        const Icon = ICONS[chip.icon] || ICONS.Building2;
        const colorCls = KIND_COLORS[chip.kind] || "border-white/15 bg-white/5 text-[#a0aabf]";
        const isCurrent = i === 0;
        return (
          <Link
            key={`${chip.kind}-${chip.id}-${i}`}
            to={chip.url || "#"}
            className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono transition ${colorCls} ${isCurrent ? "ring-1 ring-white/20" : "opacity-90 hover:opacity-100"}`}
            data-testid={`entity-trail-${chip.kind}`}
            title={`${chip.kind.replace(/_/g, " ")} → ${chip.label}`}
          >
            <Icon className="w-2.5 h-2.5" />
            <span className="max-w-[20ch] truncate">{chip.label}</span>
          </Link>
        );
      })}
      {hasWarning && (
        <span
          className="shrink-0 inline-flex items-center gap-1 text-[10px] text-amber-300 ml-1"
          title={trail.warnings.join(" · ")}
          data-testid="entity-trail-warning"
        >
          <AlertTriangle className="w-3 h-3" />
          <span className="hidden sm:inline">{trail.warnings[0].slice(0, 80)}{trail.warnings[0].length > 80 ? "…" : ""}</span>
        </span>
      )}
      {trail.fixes_applied?.length > 0 && (
        <span
          className="shrink-0 text-[10px] text-emerald-300 ml-1"
          title={trail.fixes_applied.join(" · ")}
          data-testid="entity-trail-fixes"
        >
          ✓ {trail.fixes_applied.length} link{trail.fixes_applied.length === 1 ? "" : "s"} fixed
        </span>
      )}
      {cust && (
        <button
          type="button"
          onClick={() => setFamilyOpen(true)}
          className="shrink-0 ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-400/30 bg-emerald-400/5 text-emerald-300 text-[10px] hover:bg-emerald-400/10 transition"
          title="Show whole family tree for this customer"
          data-testid="entity-trail-family-btn"
        >
          <Network className="w-3 h-3" /> Family tree
        </button>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ml-auto shrink-0 text-[#647087] hover:text-white"
        title="Hide trail (until next page)"
        data-testid="entity-trail-dismiss"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
    {familyOpen && cust && (
      <FamilyTreeModal customerChip={cust} onClose={() => setFamilyOpen(false)} />
    )}
    </>
  );
}

/* ─── Family-tree modal — every entity under one customer, by kind ────
   Triggered from the breadcrumb's "Family tree" button. Lazy-loads
   /api/entity-family/{customer_id}. Quick-filter input at the top lets
   the user grep across labels. Each row is a link to the same target
   the breadcrumb chips would go to. */
function FamilyTreeModal({ customerChip, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/entity-family/${customerChip.id}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load family tree");
      }
    })();
    return () => { cancelled = true; };
  }, [customerChip.id]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filteredFamily = useMemo(() => {
    if (!data?.family) return {};
    const q = search.trim().toLowerCase();
    if (!q) return data.family;
    const out = {};
    for (const [kind, items] of Object.entries(data.family)) {
      const hits = items.filter((it) => (it.label || "").toLowerCase().includes(q));
      if (hits.length) out[kind] = hits;
    }
    return out;
  }, [data?.family, search]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4 hv-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="entity-family-modal"
    >
      <div className="hv-card w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
          <Network className="w-5 h-5 text-emerald-300" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Family tree</div>
            <h3 className="font-display text-lg font-bold truncate">{customerChip.label}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="entity-family-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-[#647087]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Quick-filter across this customer's universe…"
            className="hv-input border-0 bg-transparent flex-1"
            data-testid="entity-family-search"
            autoFocus
          />
          {data?.totals && (
            <span className="text-[10px] text-[#647087] font-mono shrink-0">
              {Object.values(data.totals).reduce((a, b) => a + b, 0)} items
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {err && <div className="text-xs text-rose-300">{err}</div>}
          {!data && !err && <div className="text-xs text-[#647087]">Loading…</div>}
          {data && Object.keys(filteredFamily).length === 0 && (
            <div className="text-xs text-[#647087] text-center py-6">
              {search ? "No matches in this customer's universe." : "No linked entities yet — this customer has no jobs, tickets, photos or other records."}
            </div>
          )}
          {data && Object.entries(filteredFamily).map(([kind, items]) => {
            const Icon = ICONS[(items[0]?.icon)] || ICONS.Building2;
            const colorCls = KIND_COLORS[kind] || "border-white/15 bg-white/5 text-[#a0aabf]";
            const total = data.totals?.[kind] || items.length;
            return (
              <section key={kind} className="mb-4" data-testid={`entity-family-section-${kind}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wider ${colorCls}`}>
                    <Icon className="w-3 h-3" />
                    {kind.replace(/_/g, " ")}
                  </span>
                  <span className="text-[10px] text-[#647087] font-mono">{items.length}{items.length !== total ? ` / ${total}` : ""}</span>
                </div>
                <ul className="space-y-1">
                  {items.map((it) => (
                    <li key={`${it.kind}-${it.id}`}>
                      <Link
                        to={it.url || "#"}
                        onClick={onClose}
                        className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-white/5 transition group"
                        data-testid={`entity-family-link-${it.kind}-${it.id}`}
                      >
                        <Icon className="w-3 h-3 text-[#647087] group-hover:text-[#1FB6C1]" />
                        <span className="flex-1 truncate">{it.label}</span>
                        <ChevronRight className="w-3 h-3 text-[#647087] opacity-0 group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>

        <footer className="px-4 py-2 border-t border-white/5 text-[10px] text-[#647087] flex items-center justify-between">
          <span>Press Esc to close · click any item to navigate</span>
          <Link
            to={customerChip.url}
            onClick={onClose}
            className="text-[#1FB6C1] hover:underline"
            data-testid="entity-family-open-customer"
          >
            Open customer →
          </Link>
        </footer>
      </div>
    </div>
  );
}
